import { Component, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { EventService } from '../core/services/event.service';
import { GuestService } from '../core/services/guest.service';
import { EncryptionService } from '../core/services/encryption.service';
import { AnalyticsService } from '../core/services/analytics.service';

@Component({
  selector: 'app-demo-landing',
  templateUrl: './demo-landing.component.html',
  styleUrls: ['./demo-landing.component.scss'],
})
export class DemoLandingComponent implements OnDestroy {
  eventName: string = '';
  eventDate: string = '';
  coverPreview: string | null = null;

  // ── Cover adjustment ──────────────────────────────────────────────────────
  isAdjusting = false;
  posX = 50;   // 0–100 %
  posY = 50;

  get coverPositionStyle(): string {
    return `${this.posX}% ${this.posY}%`;
  }

  private dragging = false;
  private lastX = 0;
  private lastY = 0;
  private readonly SENS = 0.3;

  // All bound handlers stored as fields so removeEventListener can match them
  private readonly _onMouseMove = (e: MouseEvent) => {
    if (!this.dragging) return;
    this.ngZone.run(() => this._applyDrag(e.clientX, e.clientY));
  };

  private readonly _onMouseUp = () => {
    this.dragging = false;
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
  };

  private readonly _onTouchMove = (e: TouchEvent) => {
    if (!this.dragging) return;
    e.preventDefault();
    this.ngZone.run(() => this._applyDrag(e.touches[0].clientX, e.touches[0].clientY));
  };

  private readonly _onTouchEnd = () => {
    this.dragging = false;
    document.removeEventListener('touchmove', this._onTouchMove);
    document.removeEventListener('touchend',  this._onTouchEnd);
  };

  readonly DEMO_HEX = '465db503';

  isNavigating = false;

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private afAuth: AngularFireAuth,
    private eventService: EventService,
    private guestService: GuestService,
    private encryptionService: EncryptionService,
    private analytics: AnalyticsService,
  ) {}

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup',   this._onMouseUp);
    document.removeEventListener('touchmove', this._onTouchMove);
    document.removeEventListener('touchend',  this._onTouchEnd);
  }

  // ── Cover selection ───────────────────────────────────────────────────────

  onCoverSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.ngZone.run(() => {
        this.coverPreview = reader.result as string;
        sessionStorage.setItem('demoCoverImage', this.coverPreview!);
        this.posX = 50;
        this.posY = 50;
        sessionStorage.setItem('demoCoverPosition', '50% 50%');
        this.isAdjusting = true;
      });
    };
    reader.readAsDataURL(file);
  }

  removeCover(): void {
    this.coverPreview = null;
    this.isAdjusting  = false;
    sessionStorage.removeItem('demoCoverImage');
    sessionStorage.removeItem('demoCoverPosition');
  }

  openAdjust(): void {
    this.isAdjusting = true;
  }

  confirmAdjust(): void {
    this.isAdjusting = false;
    sessionStorage.setItem('demoCoverPosition', this.coverPositionStyle);
  }

  // ── Drag: mouse ───────────────────────────────────────────────────────────

  onMouseDown(e: MouseEvent): void {
    e.preventDefault();
    this.dragging = true;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup',   this._onMouseUp);
  }

  // ── Drag: touch ───────────────────────────────────────────────────────────

  onTouchStart(e: TouchEvent): void {
    this.dragging = true;
    this.lastX = e.touches[0].clientX;
    this.lastY = e.touches[0].clientY;
    document.addEventListener('touchmove', this._onTouchMove, { passive: false });
    document.addEventListener('touchend',  this._onTouchEnd);
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  preview(): void {
    if (!this.eventName.trim() || this.isNavigating) return;
    this.isNavigating = true;

    // Save demo customisations
    if (this.coverPreview) {
      sessionStorage.setItem('demoCoverPosition', this.coverPositionStyle);
    }
    sessionStorage.setItem('demoEventName', this.eventName.trim());
    if (this.eventDate) sessionStorage.setItem('demoEventDate', this.eventDate);

    // Set up localStorage so EventDetailComponent recognises this as a demo guest
    localStorage.setItem('eventId', this.DEMO_HEX);
    localStorage.setItem('isGuestUser', 'true');
    localStorage.setItem('isDemoUser', 'true');
    localStorage.setItem('showTopBar', 'true');

    // Reuse an existing demo guest session if one is already stored
    const existingUser = localStorage.getItem('user');
    if (existingUser && existingUser !== 'null') {
      window.location.href = '/#/event-detail/true';
      return;
    }

    // Fetch numeric event ID then create a real backend guest session
    this.eventService.getOpenEventGallery().subscribe({
      next: (res: any) => {
        const numId = res?.id ?? res?.eventId ?? res?.event_id;
        if (numId) localStorage.setItem('e', numId.toString());

        this.guestService.createDemoGuestSession(this.DEMO_HEX, numId?.toString() ?? '').subscribe({
          next: (guestRes: any) => {
            // PostHog: identify with analytics_id (no PII — matches iOS and the
            // other demo/guest flows). Only ever identify with analytics_id; never
            // fall back to the Firebase uid, which would split the session.
            const analyticsId = guestRes?.analytics_id || guestRes?.data?.analytics_id;
            if (analyticsId) {
              localStorage.setItem('analytics_id', analyticsId);
              this.analytics.identify(analyticsId);
            }

            const firebaseToken = guestRes?.data?.firebase_token;
            if (!firebaseToken) {
              window.location.href = '/#/event-detail/true';
              return;
            }
            this.afAuth.signInWithCustomToken(firebaseToken)
              .then((userCredential) => {
                if (userCredential?.user?.uid) {
                  localStorage.setItem('uid', userCredential.user.uid);
                }
                let retries = 0;
                const maxRetries = 20;
                const checkUserData = () => {
                  const userData = localStorage.getItem('user');
                  if (userData && userData !== 'null') {
                    window.location.href = '/#/event-detail/true';
                  } else if (retries < maxRetries) {
                    retries++;
                    setTimeout(checkUserData, 100);
                  } else {
                    // Manually store user data and redirect
                    const encrypted = this.encryptionService.encryptData(userCredential.user);
                    localStorage.setItem('user', encrypted);
                    window.location.href = '/#/event-detail/true';
                  }
                };
                checkUserData();
              })
              .catch(() => {
                window.location.href = '/#/event-detail/true';
              });
          },
          error: () => {
            window.location.href = '/#/event-detail/true';
          },
        });
      },
      error: () => {
        window.location.href = '/#/event-detail/true';
      },
    });
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  private _applyDrag(x: number, y: number): void {
    const dx = x - this.lastX;
    const dy = y - this.lastY;
    this.lastX = x;
    this.lastY = y;
    this.posX = Math.max(0, Math.min(100, this.posX - dx * this.SENS));
    this.posY = Math.max(0, Math.min(100, this.posY - dy * this.SENS));
  }
}
